import { watch } from 'vue'

import { FederatedModule } from '@sennder/federated-module-loader'
import {
  INotificationCenterData,
  INotificationCenterMicrofrontendData,
  MountNotifications,
  SharedDataType,
} from '@sennder/senn-node-microfrontend-interfaces'
import {
  cloneReactiveToRaw,
  MicroFrontendLogger,
} from '@sennder/shell-utilities'

import { ENV } from '@/common/config'
import errorsHandler from '@/services/errors-handler'
import { getStateCallbacks, getStateData } from '@/store'
import { logger } from '@/services/logger/loggers'
import router from '@/router'
import { handlePushNotifications } from '@/mobile-handler'
import { loggerInstance } from './logger'
import { AppAnalyticsProvider } from './analyticsProvider'

export const notificationsModule = new FederatedModule<INotificationCenterData>(
  {
    devPort: 9199,
    environment: ENV || 'prod',
    moduleFederationPluginName: 'notificationcentermf',
    npmName: 'notification-center-mf',
    logger,
  }
)

let mountFunction: MountNotifications
let unmount: (() => void) | null = null
let unwatch: (() => void) | null = null

function getNotificationCenterData(): INotificationCenterData {
  return {
    shell: SharedDataType.ORCAS,
    language: getStateData().language,
    featureFlags: getStateData().featureFlags,
  }
}

async function loadNotificationsCenter(elementId: string) {
  if (!mountFunction) {
    mountFunction = (
      await notificationsModule.getModule<{ mount: MountNotifications }>(
        './notificationcentermfApp'
      )
    )?.mount
  }
  const microfrontendData: INotificationCenterMicrofrontendData = {
    data: getNotificationCenterData(),
    callbacks: {
      getAuthHeader: getStateCallbacks().getAuthHeader,
      getAuthToken: async () => {
        // old api endpoint
        if (getStateData().featureFlags['new-notification-center-old-api']) {
          return await getStateCallbacks().getAuthToken({
            audience: 'https://api.cloud.sennder.com/websocket-gateway',
          })
        }
        // new api endpoint
        const newAuthHeader = (await getStateCallbacks().getCommonHeaders())
          .Authorization
        return newAuthHeader?.split(' ')[1] || ''
      },
      getCommonHeaders: async () => {
        const headers = await getStateCallbacks().getCommonHeaders()
        if (getStateData().featureFlags['new-notification-center-old-api']) {
          headers.Authorization = await getStateCallbacks().getAuthHeader()
        }
        return headers
      },
      navigate: (route, notificationType) => {
        // handle redirect in shell
        if (
          ['AcceptNowFailed', 'Outbid', 'SpotOutbid'].includes(notificationType)
        ) {
          router.push(route)
        }
        // notify native app
        else {
          const params = typeof route === 'object' ? route.params : undefined

          handlePushNotifications({
            orderID: params?.refNo ? String(params.refNo) : '',
            type: notificationType,
          })
        }
      },
    },
    providers: {
      logger: new MicroFrontendLogger(
        {
          module: 'notifications',
          codeOwners: 'frontend-systems',
        },
        () => loggerInstance
      ),
      analytics: new AppAnalyticsProvider({
        module: 'notifications',
        submodule: '',
      }),
    },
  }
  const mountResult = mountFunction(`#${elementId}`, microfrontendData)

  unwatch = watch(
    () => getStateData(),
    async () => {
      mountResult.onSharedDataChanged(
        cloneReactiveToRaw(getNotificationCenterData())
      )
    },
    { deep: true }
  )

  unmount = mountResult.unmount
}

export async function initNotifications(elementId: string) {
  try {
    await loadNotificationsCenter(elementId)
  } catch (error: any) {
    errorsHandler(error)
  }
}

export function stopNotifications() {
  unwatch?.()
  unmount?.()
}
